/**
 * Pure logic for the content organiser on Home (spec 002): item keys, the
 * folder route parameter, name sorting, unique default names, breadcrumbs and
 * the drop rule.
 */
import { isSelfOrDescendant } from '../media/mediaListing';

export type ItemKind = 'folder' | 'scene' | 'story';

export interface ItemRef {
  kind: ItemKind;
  id: number;
}

export const itemKey = (ref: ItemRef) => `${ref.kind}-${ref.id}`;

export function parseItemKey(key: string): ItemRef {
  const [kind, id] = key.split('-');
  return { kind: kind as ItemKind, id: Number(id) };
}

/**
 * `/folder/:folderId` → the folder id, `null` at root (no parameter), or
 * `'invalid'` for anything that is not a positive integer (`/folder/abc`).
 */
export function parseFolderParam(param: string | undefined): number | null | 'invalid' {
  if (param === undefined) return null;
  if (!/^\d+$/.test(param)) return 'invalid';
  const id = Number(param);
  return id > 0 ? id : 'invalid';
}

/** By name, case-insensitive (numbers in natural order), unnamed last. */
export function sortByName<T extends { name?: string | null }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const an = a.name?.trim() ?? '';
    const bn = b.name?.trim() ?? '';
    if (!an || !bn) return an ? -1 : bn ? 1 : 0;
    return an.localeCompare(bn, undefined, { sensitivity: 'base', numeric: true });
  });
}

/**
 * `base` when no sibling has it, else `base 2`, `base 3`, … (the first free
 * one). Compared case-insensitively.
 */
export function uniqueName(base: string, siblingNames: Array<string | null | undefined>): string {
  const taken = new Set(siblingNames.map((n) => (n ?? '').trim().toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

interface TreeFolder {
  id: number;
  parentId?: number | null;
}

/** The folders from the top level down to `folderId` (empty at root or when unknown). */
export function breadcrumbPath<T extends TreeFolder>(folders: T[], folderId: number | null): T[] {
  const path: T[] = [];
  const seen = new Set<number>();
  let current = folderId;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    const folder = folders.find((f) => f.id === current);
    if (!folder) break;
    path.unshift(folder);
    current = folder.parentId ?? null;
  }
  return path;
}

/** A folder cannot be dropped into itself or one of its descendants; scenes and stories can go anywhere. */
export function canDropInto(folders: TreeFolder[], items: ItemRef[], target: number | null): boolean {
  if (items.length === 0) return false;
  return items.every((item) => item.kind !== 'folder' || !isSelfOrDescendant(folders, item.id, target));
}
